import { createSignal, createMemo, Show, type Component } from "solid-js";
import { PanelLayout, type SubTab } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { usePanel } from "@/shared/use-panel";
import { useWs } from "@/shared/ws";
import { registerPanel } from "@/panel-registry";
import { PanelBadge } from "@/shared/panel-primitives";

// ─── Schémas (alignés sur RemoteWS.Plugin.WinUpdate / WinUpdateTools.cs) ───

interface PendingUpdate {
  title: string;
  kb: string;
  severity: string | null;
  size_mb: number;
  categories: string[];
}

interface PendingList {
  count: number;
  updates: PendingUpdate[];
}

interface HistoryEntry {
  date: string;
  title: string;
  kb: string;
  result_code: number;
}

interface HistoryList {
  count: number;
  history: HistoryEntry[];
}

// Côté C# : winupdate_install accepte un seul `kb` (ou rien = toutes).
// Pour installer une sélection multiple, on enchaîne les appels.
interface InstallResult {
  jobId?: string;
  async?: boolean;
  status?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

// Mapping codes résultat WUA (IUpdateHistoryEntry.ResultCode)
// 0=NotStarted 1=InProgress 2=Succeeded 3=SucceededWithErrors 4=Failed 5=Aborted
function resultLabel(code: number): { text: string; level: "ok" | "warn" | "danger" | "muted" } {
  switch (code) {
    case 2:
      return { text: "Succès", level: "ok" };
    case 3:
      return { text: "Succès partiel", level: "warn" };
    case 4:
      return { text: "Échec", level: "danger" };
    case 5:
      return { text: "Annulée", level: "danger" };
    case 1:
      return { text: "En cours", level: "muted" };
    default:
      return { text: "Non démarrée", level: "muted" };
  }
}

function severityBadge(sev: string | null | undefined): { text: string; level: "ok" | "warn" | "danger" | "muted" } {
  const s = (sev || "").trim();
  if (!s) return { text: "—", level: "muted" };
  const norm = s.toLowerCase();
  if (norm === "critical") return { text: s, level: "danger" };
  if (norm === "important") return { text: s, level: "warn" };
  if (norm === "moderate") return { text: s, level: "warn" };
  if (norm === "low") return { text: s, level: "ok" };
  return { text: s, level: "muted" };
}

function formatDate(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  if (!/^\d{4}-\d{2}-\d{2}T/.test(s)) return s;
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

function formatSize(mb: number | null | undefined): string {
  if (mb == null || isNaN(Number(mb))) return "—";
  const n = Number(mb);
  if (n >= 1024) return `${(n / 1024).toFixed(2)} Go`;
  return `${n.toFixed(1)} Mo`;
}

// ─── Composant ────────────────────────────────────────────────────────────

const WinUpdatePanel: Component = () => {
  const ws = useWs();
  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const [selected, setSelected] = createSignal<Set<string>>(new Set());
  const [installing, setInstalling] = createSignal(false);
  const [installMessage, setInstallMessage] = createSignal<string | null>(null);

  const pendingQuery = createToolQuery<PendingList>(() => ({
    tool: "winupdate_list_pending",
    params: {},
    enabled: enabled(),
    // Polling toutes les 30s quand une install est en cours, sinon manuel.
    refetchInterval: installing() ? 30_000 : 0,
  }));

  const historyQuery = createToolQuery<HistoryList>(() => ({
    tool: "winupdate_history",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const panel = usePanel(
    () => ({ isFetching: pendingQuery.isFetching, error: pendingQuery.error }),
    () => ({ isFetching: historyQuery.isFetching, error: historyQuery.error }),
  );

  const pendingRows = createMemo<PendingUpdate[]>(() => pendingQuery.data?.updates ?? []);
  const historyRows = createMemo<HistoryEntry[]>(() => {
    const rows = [...(historyQuery.data?.history ?? [])];
    rows.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    return rows;
  });

  // ─── Sélection ────────────────────────────────────────────────────────

  function toggleRow(kb: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(kb);
      else next.delete(kb);
      return next;
    });
  }

  function toggleAll(checked: boolean) {
    if (!checked) {
      setSelected(new Set<string>());
      return;
    }
    const all = new Set<string>();
    for (const r of pendingRows()) {
      if (r.kb) all.add(r.kb);
    }
    setSelected(all);
  }

  const allSelected = createMemo(() => {
    const rows = pendingRows().filter((r) => !!r.kb);
    if (rows.length === 0) return false;
    const sel = selected();
    return rows.every((r) => sel.has(r.kb));
  });

  // ─── Installation ─────────────────────────────────────────────────────

  async function runInstall(mode: "selection" | "all") {
    const sel = Array.from(selected());
    const targets: (string | null)[] = mode === "all" ? [null] : sel.length > 0 ? sel : [];

    if (mode === "selection" && targets.length === 0) {
      panel.setActionStatus({ text: "Aucune mise à jour sélectionnée.", level: "warn" });
      return;
    }

    const summary =
      mode === "all"
        ? `Installer TOUTES les mises à jour en attente (${pendingRows().length}) ?`
        : `Installer ${targets.length} mise(s) à jour sélectionnée(s) ?\n${sel.join(", ")}`;

    const ok = await panel.confirm.confirm({
      title: "Installer les mises à jour Windows",
      body: `${summary}\n\nL'opération peut prendre plusieurs minutes et provoquer un redémarrage du host.`,
      level: "danger",
      confirmLabel: "Installer",
      entityName: mode === "all" ? "TOUTES" : sel.join(","),
    });
    if (!ok) return;

    setInstalling(true);
    setInstallMessage(
      "Installation lancée — peut prendre plusieurs minutes et redémarrer le host. La liste des MAJ en attente se rafraîchit automatiquement toutes les 30 secondes.",
    );
    panel.setActionStatus({ text: "Installation en cours…", level: "info" });

    try {
      // winupdate_install C# = un seul kb par appel → on enchaîne.
      for (let i = 0; i < targets.length; i++) {
        const kb = targets[i];
        const label = kb ?? "toutes";
        panel.setActionStatus({
          text: `Installation ${i + 1}/${targets.length} — ${label}…`,
          level: "info",
        });
        const params: Record<string, unknown> = kb ? { kb } : {};
        // Le tool est async (renvoie jobId immédiatement). Timeout large pour le
        // « ack » initial, mais l'install réelle continue côté agent.
        await panel.tools.call<InstallResult>("winupdate_install", params, {
          userConfirmedAt: new Date().toISOString(),
          timeoutMs: 120_000,
        });
      }

      panel.setActionStatus({
        text: "Toutes les commandes d'installation ont été envoyées. Vérifiez la liste et l'historique.",
        level: "info",
      });
      setSelected(new Set<string>());
      // Rafraîchit immédiatement la liste et l'historique.
      await pendingQuery.refetch();
      await historyQuery.refetch();
    } catch (err) {
      panel.setActionStatus({
        text: `Erreur installation : ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    } finally {
      // On arrête le polling agressif après quelques minutes.
      setTimeout(() => {
        setInstalling(false);
        setInstallMessage(null);
      }, 5 * 60_000);
    }
  }

  // ─── Colonnes : En attente ────────────────────────────────────────────

  const pendingColumns: Column<PendingUpdate>[] = [
    {
      id: "select",
      header: () => (
        <input
          type="checkbox"
          checked={allSelected()}
          onChange={(e) => toggleAll(e.currentTarget.checked)}
          title="Tout sélectionner"
        />
      ),
      cell: (info) => {
        const row = info.row.original;
        const kb = row.kb;
        return (
          <input
            type="checkbox"
            checked={!!kb && selected().has(kb)}
            disabled={!kb}
            onChange={(e) => kb && toggleRow(kb, e.currentTarget.checked)}
          />
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: "kb",
      header: "KB",
      cell: (info) => {
        const v = String(info.getValue() ?? "");
        return <>{v ? <code style={{ "font-size": "11px" }}>{v}</code> : <PanelBadge intent="muted">—</PanelBadge>}</>;
      },
    },
    {
      accessorKey: "title",
      header: "Titre",
      cell: (info) => <span title={String(info.getValue() ?? "")}>{String(info.getValue() ?? "")}</span>,
    },
    {
      id: "categories",
      header: "Catégorie",
      accessorFn: (r) => (r.categories || []).join(", "),
      cell: (info) => {
        const v = String(info.getValue() ?? "");
        return <>{v ? v : <PanelBadge intent="muted">—</PanelBadge>}</>;
      },
    },
    {
      accessorKey: "size_mb",
      header: "Taille",
      cell: (info) => formatSize(info.getValue() as number),
    },
    {
      accessorKey: "severity",
      header: "Sévérité",
      cell: (info) => {
        const b = severityBadge(info.getValue() as string | null);
        return <PanelBadge intent={b.level}>{b.text}</PanelBadge>;
      },
    },
  ];

  // ─── Colonnes : Historique ────────────────────────────────────────────

  const historyColumns: Column<HistoryEntry>[] = [
    {
      accessorKey: "date",
      header: "Date",
      cell: (info) => formatDate(info.getValue()),
    },
    {
      accessorKey: "kb",
      header: "KB",
      cell: (info) => {
        const v = String(info.getValue() ?? "");
        return <>{v ? <code style={{ "font-size": "11px" }}>{v}</code> : <PanelBadge intent="muted">—</PanelBadge>}</>;
      },
    },
    {
      accessorKey: "title",
      header: "Titre",
      cell: (info) => <span title={String(info.getValue() ?? "")}>{String(info.getValue() ?? "")}</span>,
    },
    {
      accessorKey: "result_code",
      header: "Résultat",
      cell: (info) => {
        const code = Number(info.getValue() ?? 0);
        const b = resultLabel(code);
        return <PanelBadge intent={b.level}>{b.text}</PanelBadge>;
      },
    },
    {
      id: "description",
      header: "Description",
      accessorFn: (r) => {
        const code = Number(r.result_code ?? 0);
        return `${resultLabel(code).text} (code ${code})`;
      },
      cell: (info) => <span style={{ color: "var(--muted, #888)" }}>{String(info.getValue() ?? "")}</span>,
    },
  ];

  // ─── SubTabs ──────────────────────────────────────────────────────────

  const subtabs: SubTab[] = [
    {
      id: "pending",
      label: "📥 En attente",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <button
              type="button"
              class="btn btn--xs btn--primary"
              disabled={selected().size === 0}
              onClick={() => runInstall("selection")}
            >
              Installer sélection ({selected().size})
            </button>
            <button
              type="button"
              class="btn btn--xs btn--danger"
              disabled={pendingRows().length === 0}
              onClick={() => runInstall("all")}
            >
              Installer tout
            </button>
            <button
              type="button"
              class="btn btn--xs btn--ghost"
              onClick={() => pendingQuery.refetch()}
              title="Rafraîchir"
            >
              ↺ Rafraîchir
            </button>
            <Show when={pendingQuery.data}>
              <span style={{ "margin-left": "auto" }}>
                <PanelBadge intent="info">{pendingRows().length} en attente</PanelBadge>
              </span>
            </Show>
          </div>

          <Show when={installMessage()}>
            <div
              class="panel-banner"
              style={{
                padding: "8px 12px",
                margin: "8px 0",
                "border-left": "3px solid var(--warn, #d97706)",
                background: "rgba(217, 119, 6, 0.08)",
                "font-size": "12px",
                "line-height": "1.4",
              }}
            >
              {installMessage()}
            </div>
          </Show>

          <DataTable<PendingUpdate>
            rows={pendingRows()}
            columns={pendingColumns}
            emptyMessage="Aucune mise à jour en attente."
          />
        </>
      ),
    },
    {
      id: "history",
      label: "🕒 Historique",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--ghost" onClick={() => historyQuery.refetch()}>
              ↺ Rafraîchir
            </button>
            <Show when={historyQuery.data}>
              <span style={{ "margin-left": "auto" }}>
                <PanelBadge intent="info">{historyRows().length} entrée(s)</PanelBadge>
              </span>
            </Show>
          </div>
          <DataTable<HistoryEntry>
            rows={historyRows()}
            columns={historyColumns}
            emptyMessage="Aucun historique disponible."
          />
        </>
      ),
    },
  ];

  return <PanelLayout title="Windows Update" subtabs={subtabs} status={panel.status()} />;
};

registerPanel("winupdate", WinUpdatePanel);
export default WinUpdatePanel;
